import { useContext, useEffect, useRef, useState } from "react";
import { ParagraphText, UBAppModal, UBOTPInput } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { useAuthContext } from "../../../context/AuthContext";
import { handleApi } from "../../../apis";
import { ACCOUNT_PIN_RESET, CHECK_PIN_GET } from "../../../apis/configuration";
import Cookies from "js-cookie";
import { formatNum } from "../../../oscar-pos-core/constants";
import modalStyles from "./VerifyPinModal.module.css";

/**
 * Shared PIN-confirmation modal used to gate the final "confirm transaction"
 * step across the wallet's payment flows (SendMoney, Easyload, Bills, Vouchers),
 * so PIN verification behaves identically everywhere it is required.
 */
const VerifyPinModal = ({
  open,
  handleClose,
  amount = 0,
  onVerified,
  forgotPinAnalyticsEvent = "w_forgot_pin",
}) => {
  const [I18n] = useContext(LanguageContext);
  const [error, setError] = useState("");
  const [PIN, setPIN] = useState("");
  const [loading, setLoading] = useState(false);
  const isDisabled = !(PIN.length === 4) || loading;
  const { showSnackbar } = useSnackbar();
  const { handleLogoutInDB } = useAuthContext();
  const navigate = useNavigate();
  const onChange = (val) => setPIN(val);

  const otpContainerRef = useRef(null);

  const focusFirstInput = () => {
    setTimeout(() => {
      const el = otpContainerRef.current?.querySelector(
        "input:not([disabled])",
      );
      if (el) el.focus();
    }, 100);
  };

  const handleForgotPIN = async () => {
    const phone = localStorage.getItem("phone_number");
    try {
      setLoading(true);
      triggerLogEvent(analytics, forgotPinAnalyticsEvent);
      const response = await handleApi(
        ACCOUNT_PIN_RESET,
        { phone_number: phone },
        null,
        navigate,
        { "Api-Key": process.env.REACT_APP_API_KEY },
      );
      const isVerified = response?.verified;
      if (Boolean(isVerified)) {
        sessionStorage.setItem(
          "forgotPinVerify",
          JSON.stringify({ phone: phone || "" }),
        );
      }
      await handleLogoutInDB(navigate);
      if (Boolean(isVerified))
        navigate("/", {
          replace: true,
          state: { isVerifyAccountScreen: true, phoneNumber: phone },
        });
      else navigate("/ForgotPin", { state: { phone } });
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const submitVerifyPIN = async () => {
    setError("");
    if (PIN.length !== 4) return;
    try {
      setLoading(true);
      const response = await handleApi(
        CHECK_PIN_GET,
        { rnp_verify: true, pin: PIN },
        null,
        navigate,
        { "Web-Token": Cookies.get("deviceId") },
      );
      if (response.success) {
        setLoading(false);
        setPIN("");
        handleClose();
        onVerified?.();
      } else {
        setLoading(false);
        setPIN("");
        focusFirstInput();
        if (response.is_locked) {
          showSnackbar({
            message: I18n.your_account_has_been_locked,
            severity: "error",
            icon: "",
          });
        }
        if (response.message && response.attempts_left) {
          setError(
            `${response.message} (${response.attempts_left} ${I18n.tries_left})`,
          );
        }
      }
    } catch (error) {
      setPIN("");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPIN("");
      setError("");
    }
  }, [open]);

  return (
    <UBAppModal
      id="verify_pin_modal"
      size="lg"
      className={modalStyles.verifyPinRoot}
      open={open}
      onClose={() => {
        if (!loading) handleClose();
      }}
      header={{
        title: (
          <ParagraphText className="d-flex align-items-center fontSize20 fontWeight500 newFontColor">
            {I18n.verify_PIN}
          </ParagraphText>
        ),
        handleClose,
        dont_show_cross_icon: true,
      }}
      footer={[
        {
          data: {
            onClick: handleClose,
            title: I18n.back,
            id: "verify_pin_back_btn",
            className: `ubSecondaryButton ${modalStyles.backBtn}`,
            disabled: loading,
          },
        },
        {
          data: {
            id: "verify_pin_confirm_btn",
            className: `ubPrimaryButton ${modalStyles.confirmBtn}`,
            title: `${I18n.confirm} RS. ${formatNum(amount)}`,
            onClick: submitVerifyPIN,
            disabled: isDisabled,
          },
        },
      ]}
    >
      <Box
        className={modalStyles.body}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isDisabled) submitVerifyPIN();
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backgroundColor: "var(--modalLoadingOverlay)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <CircularProgress size={32} sx={{ color: "var(--mainColor)" }} />
          </Box>
        )}
        <div ref={otpContainerRef}>
          <UBOTPInput
            label={I18n.enter_pin}
            className={modalStyles.otpInput}
            value={PIN}
            onChangeQuery={onChange}
            shouldAutoFocus
            invalid={!!error}
            helperText={error}
            isInputSecure={true}
            id="verify_pin_input"
            disabled={loading}
          />
        </div>
        <ParagraphText
          className={`fontSize16 ${modalStyles.forgotPin}`}
          onClick={handleForgotPIN}
          id="verify_pin_modal_forgot_pin"
        >
          {I18n.forgot_pin}
        </ParagraphText>
      </Box>
    </UBAppModal>
  );
};

export default VerifyPinModal;
